import {
    AccountBalancesWidget,
    ChartWidget,
    CreditCardRateCalloutWidget,
    DraftedHandoffCard,
    ExistingGoalsBadgesWidget,
    GoalSummaryCardWidget,
    IncomeSplitWidget,
    InfoCardsWidget,
    InlineDataTableWidget,
    MoneyMapWidget,
    SpendGoalsWidget,
    SproutWidgetFrame,
} from '@fruitful/agent-ui';
import {
    type AccountBalancesWidgetProps,
    type CardDetailsWidgetProps,
    type ChartWidgetProps,
    type EmailSupportWidgetProps,
    type ExistingGoalsBadgesWidgetProps,
    type GoalSummaryCardWidgetProps,
    type IncomeSplitWidgetProps,
    type InfoCardsWidgetProps,
    type InlineDataTableWidgetProps,
    type MoneyMapWidgetProps,
    type SendToGuideWidgetProps,
    type SpendGoalsWidgetProps,
    WidgetType,
} from '@fruitful/types';
import { LinearGradient } from 'expo-linear-gradient';
import React, { type ReactNode } from 'react';
import ErrorBoundary from 'react-native-error-boundary';
import {
    BodySmallRegular,
    GRADIENT_COLOR_BOTTOM,
    GRADIENT_COLOR_TOP,
    YStack,
} from 'smoothie';

import { type TranscriptWidgetSources } from '../../utils/transcriptWidgetSources';
import { TranscriptCardDetails } from './TranscriptCardDetails';

const WIDGET_CANVAS_MAX_WIDTH = 480;

const MONO_STYLE = {
    fontFamily: 'monospace',
    fontSize: 11.5,
    lineHeight: 17,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
} as const;

const missingToolResultReason = (toolName: string) =>
    `Renders from the member's live data. No ${toolName} result was recorded before this widget, so it can't be rebuilt here.`;
const NOT_PORTED_REASON = 'Not available in Grove yet.';
const INCOME_SPLIT_REBUILT_NOTE =
    'Approximate: rebuilt from the latest overviewIncomeSplit_get result before this widget. The data the member saw may have differed.';
const CARD_DETAILS_REBUILT_NOTE =
    "Approximate: Grove's version of the card tile, rebuilt from the latest allBankingAccountDetails_get result before this widget. The card shown and its layout may differ from what the member saw.";

interface TranscriptWidgetProps {
    widget: WidgetType;
    props: Record<string, unknown> | null;
    sources?: TranscriptWidgetSources;
    memberUserId?: string | null;
}

/**
 * Paints the member-app chat gradient behind a widget (the widgets' frosted
 * surfaces are designed to sit on it). `chrome` mirrors how the member app
 * wraps each widget: none, the Sprout frame, or the frosted Sprout frame.
 */
const WidgetCanvas = ({
    chrome = 'frame',
    contentPadding,
    children,
}: {
    chrome?: 'none' | 'frame' | 'frosted';
    contentPadding?: number;
    children: ReactNode;
}) => (
    <LinearGradient
        colors={[GRADIENT_COLOR_TOP, GRADIENT_COLOR_BOTTOM]}
        style={{
            width: '100%',
            maxWidth: WIDGET_CANVAS_MAX_WIDTH,
            borderRadius: 16,
            paddingVertical: 16,
        }}
    >
        {chrome === 'none' ? (
            children
        ) : (
            <SproutWidgetFrame
                frosted={chrome === 'frosted'}
                contentPadding={contentPadding}
            >
                {children}
            </SproutWidgetFrame>
        )}
    </LinearGradient>
);

/**
 * Flags a widget Grove rebuilt from thread tool results instead of rendering
 * from its own props, so it may not match what the member saw.
 */
const RebuiltWidget = ({
    note,
    children,
}: {
    note: string;
    children: ReactNode;
}) => (
    <YStack width="100%" maxWidth={WIDGET_CANVAS_MAX_WIDTH} gap="$1">
        <YStack padding="$2" borderRadius="$2" backgroundColor="$orange300">
            <BodySmallRegular color="$orange1100">{note}</BodySmallRegular>
        </YStack>
        {children}
    </YStack>
);

const UnrenderedWidget = ({
    reason,
    props,
}: {
    reason: string;
    props: Record<string, unknown> | null;
}) => (
    <YStack
        width="100%"
        maxWidth={WIDGET_CANVAS_MAX_WIDTH}
        gap="$1"
        padding="$2"
        borderWidth={1}
        borderStyle="dashed"
        borderColor="$gray400"
        borderRadius="$2"
        backgroundColor="$gray100"
    >
        <BodySmallRegular color="$gray800">{reason}</BodySmallRegular>
        {props != null ? (
            <BodySmallRegular color="$gray1100" style={MONO_STYLE}>
                {JSON.stringify(props, null, 2)}
            </BodySmallRegular>
        ) : null}
    </YStack>
);

const WidgetContent = ({
    widget,
    props,
    sources,
    memberUserId,
}: TranscriptWidgetProps) => {
    switch (widget) {
        case WidgetType.InlineDataTable:
            return (
                <WidgetCanvas>
                    <InlineDataTableWidget
                        {...(props as unknown as InlineDataTableWidgetProps)}
                    />
                </WidgetCanvas>
            );
        case WidgetType.Chart:
            return (
                <WidgetCanvas chrome="frosted">
                    <ChartWidget {...(props as unknown as ChartWidgetProps)} />
                </WidgetCanvas>
            );
        case WidgetType.MoneyMap:
            return (
                <WidgetCanvas chrome="frosted">
                    <MoneyMapWidget
                        {...(props as unknown as MoneyMapWidgetProps)}
                    />
                </WidgetCanvas>
            );
        case WidgetType.AccountBalances:
            return (
                <WidgetCanvas>
                    <AccountBalancesWidget
                        {...(props as unknown as AccountBalancesWidgetProps)}
                    />
                </WidgetCanvas>
            );
        case WidgetType.InfoCards:
            return (
                <WidgetCanvas chrome="none">
                    <InfoCardsWidget
                        {...(props as unknown as InfoCardsWidgetProps)}
                    />
                </WidgetCanvas>
            );
        case WidgetType.GoalSummaryCard:
            return (
                <WidgetCanvas>
                    <GoalSummaryCardWidget
                        {...(props as unknown as GoalSummaryCardWidgetProps)}
                    />
                </WidgetCanvas>
            );
        case WidgetType.ExistingGoalsBadges:
            return (
                <WidgetCanvas chrome="none">
                    <ExistingGoalsBadgesWidget
                        {...(props as unknown as ExistingGoalsBadgesWidgetProps)}
                    />
                </WidgetCanvas>
            );
        case WidgetType.CreditCardRateCallout:
            return (
                <WidgetCanvas>
                    <CreditCardRateCalloutWidget />
                </WidgetCanvas>
            );
        case WidgetType.SpendGoals:
            return (
                <WidgetCanvas>
                    <SpendGoalsWidget
                        {...(props as unknown as SpendGoalsWidgetProps)}
                    />
                </WidgetCanvas>
            );
        case WidgetType.SendToGuide: {
            const { messageBody } = props as unknown as SendToGuideWidgetProps;
            return (
                <WidgetCanvas chrome="frosted">
                    <DraftedHandoffCard
                        label="Message to your guide"
                        body={messageBody}
                        actionLabel="Send in Fruitful chat"
                    />
                </WidgetCanvas>
            );
        }
        case WidgetType.EmailSupport: {
            const { subject, body } =
                props as unknown as EmailSupportWidgetProps;
            return (
                <WidgetCanvas chrome="frosted">
                    <DraftedHandoffCard
                        label="Email to support"
                        title={subject}
                        body={body}
                        actionLabel="Email support"
                    />
                </WidgetCanvas>
            );
        }
        case WidgetType.CardDetails:
            if (sources?.bankAccounts == null) {
                return (
                    <UnrenderedWidget
                        reason={missingToolResultReason(
                            'allBankingAccountDetails_get',
                        )}
                        props={props}
                    />
                );
            }
            return (
                <RebuiltWidget note={CARD_DETAILS_REBUILT_NOTE}>
                    <WidgetCanvas>
                        <TranscriptCardDetails
                            props={props as unknown as CardDetailsWidgetProps}
                            accounts={sources.bankAccounts}
                            memberUserId={memberUserId}
                        />
                    </WidgetCanvas>
                </RebuiltWidget>
            );
        case WidgetType.IncomeSplit:
            if (sources?.incomeSplitOverview == null) {
                return (
                    <UnrenderedWidget
                        reason={missingToolResultReason(
                            'overviewIncomeSplit_get',
                        )}
                        props={props}
                    />
                );
            }
            return (
                <RebuiltWidget note={INCOME_SPLIT_REBUILT_NOTE}>
                    <WidgetCanvas chrome="frosted" contentPadding={0}>
                        <IncomeSplitWidget
                            {...(props as unknown as IncomeSplitWidgetProps)}
                            data={sources.incomeSplitOverview}
                        />
                    </WidgetCanvas>
                </RebuiltWidget>
            );
        case WidgetType.MultiSelectInput:
        case WidgetType.SliderInput:
        case WidgetType.DebtBalanceSliders:
        case WidgetType.IncomeCollection:
        case WidgetType.IncomeInput:
        case WidgetType.EstimatedBills:
        case WidgetType.SavingsGoals:
        case WidgetType.SavingsGoalAmounts:
        case WidgetType.DraftedMessageButton:
            // These render in the member's composer, not as a chat bubble.
            // The orange widget label is the indicator they rendered; the
            // member's answer (often the prefilled display text) is the next
            // message. Props stay off this row — they belong on the Raw tab.
            return null;
        case WidgetType.AhaMoment:
            // Member sees a money-map card that continues into results. Grove
            // only shows the orange "Aha Moment" label; props stay on the Raw tab.
            return null;
        case WidgetType.FreeToSpend:
        case WidgetType.Transactions:
            return (
                <UnrenderedWidget reason={NOT_PORTED_REASON} props={props} />
            );
        default: {
            const unhandled: never = widget;
            return (
                <UnrenderedWidget
                    reason={`Unknown widget type: ${String(unhandled)}`}
                    props={props}
                />
            );
        }
    }
};

const WidgetRenderError = () => (
    <BodySmallRegular color="$red900">
        This widget failed to render with the recorded props. See the Raw tab.
    </BodySmallRegular>
);

/** Read-only render of an agent chat widget as the member saw it. */
export const TranscriptWidget = (
    transcriptWidgetProps: TranscriptWidgetProps,
) => {
    const { widget, props } = transcriptWidgetProps;
    if (props == null && widget !== WidgetType.CreditCardRateCallout) {
        return <UnrenderedWidget reason="Widget has no props." props={null} />;
    }

    return (
        <ErrorBoundary FallbackComponent={WidgetRenderError}>
            <WidgetContent {...transcriptWidgetProps} />
        </ErrorBoundary>
    );
};
